import {
  pruneNewDocumentIds,
  replaceDocumentsForCollection,
  upsertDocumentInItems,
} from './documents-store-state'
import type { CollectionDoc, Doc } from '../../types/Doc'
import type { Dependencies } from '../../types/Dependencies'
import type {
  DocumentsLoadAllRequest,
  DocumentsLoadRequest,
  DocumentsLoadStatus,
  DocumentsSetState,
} from './documents-store'
import { UNSCOPED_DOCUMENTS_LOAD_KEY } from '@/integrations/zustand-store/features/documents/documents-store-constants'

export async function loadDocumentByIdAction({
  dependencies,
  documentId,
  set,
}: {
  dependencies: Dependencies
  documentId: string
  set: DocumentsSetState
}): Promise<Doc> {
  const document = await dependencies.records.get(documentId)

  if (!document.documentCollectionId) {
    throw new Error('The requested document is not a collection record.')
  }

  set((state) => ({
    items: upsertDocumentInItems(state.items, document),
  }))

  return document
}

export async function loadDocumentsAction({
  dependencies,
  payload,
  set,
}: {
  dependencies: Dependencies
  payload?: DocumentsLoadRequest
  set: DocumentsSetState
}) {
  const documentCollectionId = payload?.documentCollectionId
  const shouldSetLoadState = payload?.setLoadState ?? true
  const loadKey = getDocumentsLoadKey(documentCollectionId)

  if (shouldSetLoadState) {
    setDocumentsLoadState({ loadKey, set, status: 'loading' })
  }

  try {
    const docs = await loadDocuments({
      records: dependencies.records,
      documentCollectionId,
    })

    applyLoadedDocuments({
      docs,
      documentCollectionId,
      loadKey,
      set,
      shouldSetLoadState,
    })
  } catch (error) {
    if (shouldSetLoadState) {
      setDocumentsLoadState({
        error: getErrorMessage(error, 'Documents load failed.'),
        loadKey,
        set,
        status: 'failed',
      })
    }
  }
}

export async function loadAllDocumentsAction({
  dependencies,
  payload,
  set,
}: {
  dependencies: Dependencies
  payload?: DocumentsLoadAllRequest
  set: DocumentsSetState
}) {
  const shouldSetLoadState = payload?.setLoadState ?? true

  if (shouldSetLoadState) {
    setDocumentsLoadState({
      loadKey: UNSCOPED_DOCUMENTS_LOAD_KEY,
      set,
      status: 'loading',
    })
  }

  try {
    const docs = await loadAllDocuments({ records: dependencies.records })

    if (shouldSetLoadState) {
      setDocumentsLoadState({
        docs,
        loadKey: UNSCOPED_DOCUMENTS_LOAD_KEY,
        set,
        status: 'succeeded',
      })
    } else {
      set({ items: docs })
    }
  } catch (error) {
    if (shouldSetLoadState) {
      setDocumentsLoadState({
        error: getErrorMessage(error, 'Documents load failed.'),
        loadKey: UNSCOPED_DOCUMENTS_LOAD_KEY,
        set,
        status: 'failed',
      })
    }
  }
}

function applyLoadedDocuments({
  docs,
  documentCollectionId,
  loadKey,
  set,
  shouldSetLoadState,
}: {
  docs: Array<CollectionDoc>
  documentCollectionId?: string
  loadKey: string
  set: DocumentsSetState
  shouldSetLoadState: boolean
}) {
  if (shouldSetLoadState) {
    setDocumentsLoadState({
      docs,
      documentCollectionId,
      loadKey,
      set,
      status: 'succeeded',
    })

    return
  }

  set((state) => ({
    items: replaceDocumentsForLoadedScope({
      currentItems: state.items,
      docs,
      documentCollectionId,
    }),
    ...(documentCollectionId
      ? {
          newDocumentIdsByCollectionId: {
            ...state.newDocumentIdsByCollectionId,
            [documentCollectionId]: pruneNewDocumentIds({
              documentCollectionId,
              items: docs,
              newDocumentIdsByCollectionId: state.newDocumentIdsByCollectionId,
            }),
          },
        }
      : {}),
  }))
}

function setDocumentsLoadState({
  docs,
  error = null,
  documentCollectionId,
  loadKey,
  set,
  status,
}: {
  docs?: Array<CollectionDoc>
  error?: string | null
  documentCollectionId?: string
  loadKey: string
  set: DocumentsSetState
  status: DocumentsLoadStatus
}) {
  set((state) => ({
    ...(docs
      ? {
          items: replaceDocumentsForLoadedScope({
            currentItems: state.items,
            docs,
            documentCollectionId,
          }),
        }
      : {}),
    ...(docs && documentCollectionId
      ? {
          newDocumentIdsByCollectionId: {
            ...state.newDocumentIdsByCollectionId,
            [documentCollectionId]: pruneNewDocumentIds({
              documentCollectionId,
              items: docs,
              newDocumentIdsByCollectionId: state.newDocumentIdsByCollectionId,
            }),
          },
        }
      : {}),
    loadStatusByCollectionId: {
      ...state.loadStatusByCollectionId,
      [loadKey]: status,
    },
    loadErrorByCollectionId: {
      ...state.loadErrorByCollectionId,
      [loadKey]: error,
    },
  }))
}

async function loadDocuments({
  records,
  documentCollectionId,
}: {
  records: Dependencies['records']
  documentCollectionId?: string
}) {
  if (!documentCollectionId) {
    throw new Error('Document collection id is required to load documents.')
  }

  return records.list(documentCollectionId)
}

async function loadAllDocuments({
  records,
}: {
  records: Dependencies['records']
}) {
  return records.list()
}

function getDocumentsLoadKey(documentCollectionId?: string) {
  return documentCollectionId ?? UNSCOPED_DOCUMENTS_LOAD_KEY
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback
}

function replaceDocumentsForLoadedScope({
  currentItems,
  docs,
  documentCollectionId,
}: {
  currentItems: Array<CollectionDoc>
  docs: Array<CollectionDoc>
  documentCollectionId?: string
}) {
  if (!documentCollectionId) {
    return docs
  }

  return replaceDocumentsForCollection({
    documentCollectionId,
    items: currentItems,
    nextDocuments: docs,
  })
}
